import React, { useEffect, useState, useCallback } from "react";
import { StreamingProvider } from "@switcherstudio/switcher-api-client";
import { useIsMountedRef } from "hooks/useIsMountedRef";
import { usePlatformActions } from "hooks/usePlatformActions";
import { PlatformId } from "../types";
import { ConnectPlatformContainer } from "./ConnectPlatformContainer";
import styles from "./ConnectPlatformPage.module.scss";
import { PlatformImage } from "components/image/PlatformImage";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AppDispatch } from "store/store";
import { useParams } from "hooks/useParams";
import { usePageHeader } from "hooks/usePageHeader";
import { closeCurrentModal, setActiveModal } from "store/modal/slice";
import { Modals } from "store/modal/types";
import { AttentionModal } from "components/modal/AttentionModal";
import { client } from "api/client";
import { getInstagramConnectState } from "helpers/instagram";

export const ConnectPlatformPage: React.FC = () => {
    const { platformId } = useParams();

    const { t } = useTranslation("platforms");
    const isMountedRef = useIsMountedRef();
    const [platform, setPlatform] = useState<StreamingProvider>();
    const [extraLinkArgs, setExtraLinkArgs] = useState<object>();
    const { link, unlink, getPlatform } = usePlatformActions(
        platformId as PlatformId
    );
    const dispatch = useDispatch<AppDispatch>();
    const isInstagram = platformId === PlatformId.Instagram;
    const [facebookLinked, setFacebookLinked] = useState<boolean>();
    const instagramState =
        isInstagram && facebookLinked !== undefined
            ? getInstagramConnectState(platform?.IsLinked, facebookLinked)
            : undefined;

    usePageHeader({
        showBreadcrumbs: true,
        customCrumbs: [
            {
                label: t("page-titles:manage-platforms"),
                route: "/manage-platforms"
            },
            { label: t(`platforms:${platform?.Name}`), route: "/" }
        ]
    });

    useEffect(() => {
        async function run() {
            const res = await getPlatform();
            setPlatform(res);

            if (isInstagram) {
                try {
                    const fb =
                        await client.streamingProviders_GetStreamingProvider(
                            PlatformId.Facebook
                        );
                    setFacebookLinked(!!fb.IsLinked);
                } catch (e) {
                    setFacebookLinked(true);
                }
            }
        }

        if (isMountedRef.current) {
            run();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMountedRef]);

    const closeAttentionModal = useCallback(() => {
        dispatch(closeCurrentModal());
    }, [dispatch]);

    const disconnect = useCallback(async () => {
        await unlink();
        const res = await getPlatform();
        setPlatform(res);
        if (isInstagram) setFacebookLinked(false);
    }, [unlink, getPlatform, isInstagram]);

    const openAttentionModal = useCallback(
        () =>
            dispatch(
                setActiveModal({
                    id: Modals.AttentionModal,
                    type: Modals.AttentionModal,
                    component: (
                        <AttentionModal
                            isOpen={true}
                            setIsOpen={closeAttentionModal}
                            handleCancel={closeAttentionModal}
                            hasContinueButton={true}
                            handleContinue={disconnect}
                            continueText={t("buttons:confirm")}
                        >
                            <div>
                                {t(
                                    platformId === PlatformId.Instagram
                                        ? "platforms:confirm-instagram-disconnect"
                                        : "platforms:confirm-account-disconnect"
                                )}
                            </div>
                        </AttentionModal>
                    )
                })
            ),
        [closeAttentionModal, dispatch, t, disconnect, platformId]
    );

    const updateExtraLinkArgs = useCallback((args: object) => {
        setExtraLinkArgs((a) => {
            return { ...a, ...args };
        });
    }, []);

    return (
        <>
            {platform && (
                <div className="row justify-content-center">
                    <div className="col-lg-8 d-flex flex-column align-items-center">
                        <div className={styles["platform-image-container"]}>
                            <PlatformImage
                                platformId={platform?.Id || ""}
                                logoUrl={platform?.LogoUrl}
                            />
                        </div>
                        {platform?.IsLinked ? (
                            <button
                                className="btn btn-primary"
                                onClick={openAttentionModal}
                            >
                                {t("disconnect-account-button")}
                            </button>
                        ) : (
                            <>
                                <ConnectPlatformContainer
                                    platform={platform}
                                    updateExtraLinkArgs={updateExtraLinkArgs}
                                />
                                {instagramState === "needs-facebook" && (
                                    <p className="text-muted">
                                        {t("connect-facebook-for-instagram")}
                                    </p>
                                )}
                                {typeof link === "function" &&
                                    (!isInstagram ||
                                        instagramState === "connect") && (
                                        <button
                                            className="btn btn-primary"
                                            onClick={() => link(extraLinkArgs)}
                                        >
                                            {t(
                                                isInstagram
                                                    ? "reconnect-account-button"
                                                    : "connect-account-button"
                                            )}
                                        </button>
                                    )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};
